import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../../../../../../components/common/AppButton";
import AppConfirmationDialog from "../../../../../../components/common/AppConfirmationDialog";
import AppTextInput from "../../../../../../components/common/AppTextInput";
import { SVG } from "../../../../../../constants/assetLists";
import QUERY_KEYS from "../../../../../../constants/queryKeys";
import useScreenSize from "../../../../../../hooks/useScreenSize";
import { useOrganizationsScreenContext } from "../../../../../../context/organizations/OrganizationsScreenContextProvider";
import { useNavigate } from "react-router";
import { isSuperAdminOnly } from "../../../../../../utils";
import { useSnackbar } from "../../../../../../context/SnackbarContextProvider";
import {
  AdminLocationType,
  handleArchiveAdminLocationService,
  handleDeleteOrganizationDraftService,
  OrganizationTableRow,
} from "../../../../../../services/organizations";

function getLocationType(row: OrganizationTableRow): AdminLocationType {
  if (row.organizationLevel === "network") return "client";
  if (row.organizationLevel === "branch") return "branch";
  return "subBranch";
}

function Header() {
  const { i18n } = useTranslation();
  const isHebrew = useMemo(() => i18n.language === "heb", [i18n.language]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const {
    selectedOrganizationTableRowIds,
    setSelectedOrganizationTableRowIds,
    searchText,
    setSearchText,
    organizationRows,
  } = useOrganizationsScreenContext();

  const screenSize = useScreenSize();
  const isLarge = screenSize === "xl" || screenSize === "2xl";
  const isSuperAdminUser = isSuperAdminOnly();
  const isOneSelected = selectedOrganizationTableRowIds.length === 1;
  const selectedRows = useMemo(
    () =>
      organizationRows.filter((row) =>
        selectedOrganizationTableRowIds.includes(row.id),
      ),
    [organizationRows, selectedOrganizationTableRowIds],
  );
  const hasSelection = selectedRows.length > 0;
  const hasActiveSelected = selectedRows.some(
    (row) => row.status !== "inactive",
  );
  const isArchivedSelected = selectedRows.some((row) => row.isArchived);
  const isUnarchiveAction =
    hasSelection && selectedRows.every((row) => row.isArchived);
  const isDraftSelected =
    hasSelection && selectedRows.every((row) => row.status === "draft");
  const activeOrganizationsCount = useMemo(
    () => organizationRows.filter((row) => row.status === "active").length,
    [organizationRows],
  );
  const draftOrganizationsCount = useMemo(
    () => organizationRows.filter((row) => row.status === "draft").length,
    [organizationRows],
  );

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const archiveOrganizationRowsMutation = useMutation({
    mutationKey: [QUERY_KEYS.ARCHIVE_ADMIN_LOCATION],
    mutationFn: async ({
      rows,
      archive,
    }: {
      rows: OrganizationTableRow[];
      archive: boolean;
    }) => {
      const responses = await Promise.all(
        rows.map((row) =>
          handleArchiveAdminLocationService({
            locationType: getLocationType(row),
            locationId: row.id,
            archive,
          }),
        ),
      );

      return { responses, archive };
    },
    onSuccess: ({ responses, archive }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ADMIN_ON_BOARDING_DRAFTS],
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_CLIENT],
      });

      showSnackbar({
        message:
          responses[0]?.message ??
          (archive ? "הארגון נמחק בהצלחה" : "הארגון שוחזר בהצלחה"),
        type: "success",
      });
    },
  });

  const deleteDraftMutation = useMutation({
    mutationKey: [QUERY_KEYS.DELETE_ADMIN_ON_BOARDING_DRAFT],
    mutationFn: async (rows: OrganizationTableRow[]) => {
      const draftIds = Array.from(
        new Set(rows.map((row) => row.draftId).filter(Boolean)),
      );

      const responses = await Promise.all(
        draftIds.map((draftId) =>
          handleDeleteOrganizationDraftService({ draftId }),
        ),
      );

      return responses;
    },
    onSuccess: (responses) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ADMIN_ON_BOARDING_DRAFTS],
      });

      showSnackbar({
        message: responses[0]?.message ?? "הטיוטה נמחקה בהצלחה",
        type: "success",
      });
    },
  });

  const disableDelete =
    !hasSelection ||
    archiveOrganizationRowsMutation.isPending ||
    deleteDraftMutation.isPending ||
    (isDraftSelected || isUnarchiveAction
      ? false
      : hasActiveSelected || isArchivedSelected);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleCreateOnBoardingDraft = () => {
    if (!isSuperAdminUser) return;
    navigate("/on-boarding");
  };

  const handleEditModalOpen = () => {
    const selectedRow = organizationRows.find(
      (row) => row.id === selectedOrganizationTableRowIds[0],
    );

    if (isSuperAdminOnly() && selectedRow) {
      if (selectedRow.draftId) {
        navigate(
          `/on-boarding?draftId=${encodeURIComponent(selectedRow.draftId)}`,
        );
      }
    }
  };

  const handleConfirmationOpenClose = () => {
    setShowDeleteConfirmation((prev) => !prev);
  };

  const handleOnConfirm = () => {
    const onSuccess = () => {
      setSelectedOrganizationTableRowIds([]);
      setShowDeleteConfirmation(false);
    };

    if (isDraftSelected) {
      deleteDraftMutation.mutate(selectedRows, { onSuccess });
      return;
    }

    archiveOrganizationRowsMutation.mutate(
      {
        rows: selectedRows,
        archive: !isUnarchiveAction,
      },
      { onSuccess },
    );
  };

  return (
    <>
      <div
        className={`w-full py-4 bg-white px-4 flex flex-wrap items-center justify-between gap-3 shadow-md z-2 ${
          isLarge ? (isHebrew ? "rounded-tl-[32px]" : "rounded-tr-[32px]") : ""
        }`}
      >
        <div className="flex-1 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <AppButton
              variant="primary"
              py="12px"
              disabled={!isSuperAdminUser}
              onClick={handleCreateOnBoardingDraft}
              iconStart={
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.addWhiteIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
            >
              הוספה
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={!isOneSelected}
              iconStart={
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.editPencilWhiteIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
              onClick={handleEditModalOpen}
            >
              עריכה
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={disableDelete}
              iconStart={
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.whiteCloseIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
              onClick={handleConfirmationOpenClose}
            >
              {isUnarchiveAction ? "שחזור" : "מחיקה"}
            </AppButton>
          </div>

          <div className="flex items-center gap-3">
            <AppTextInput
              placeholder="חיפוש אירגון"
              value={searchText}
              onChange={handleSearch}
              containerClassName="!w-[499px]"
              className="bg-transparent !rounded-[28px] pl-16"
              endIcon={
                <div className="flex items-center gap-2">
                  {searchText && (
                    <div
                      onClick={() => setSearchText("")}
                      className="w-[24px] h-[24px] flex justify-center items-center rounded-full hover:bg-gray-200 cursor-pointer"
                    >
                      <img
                        src={SVG.closeDarkIcon}
                        alt="Clear"
                        className="w-[12px] h-[12px] object-contain"
                      />
                    </div>
                  )}
                  <div
                    className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                      searchText && "bg-aqua"
                    }`}
                  >
                    <img
                      src={SVG.searchIcon}
                      alt="Search"
                      className="w-[18px] h-[18px] object-contain"
                    />
                  </div>
                </div>
              }
            />
          </div>
        </div>

        <div>
          <p className="m-0 text-black">
            {draftOrganizationsCount} טיוטות | {activeOrganizationsCount}{" "}
            ארגונים פעילים
          </p>
        </div>
      </div>

      <AppConfirmationDialog
        open={showDeleteConfirmation}
        onClose={handleConfirmationOpenClose}
        onConfirm={handleOnConfirm}
        icon={
          <img
            src={SVG.deleteRedIcon}
            alt="icon"
            className="w-[48px] h-[48px] object-contain"
          />
        }
        title="שים לב"
        subTitle={
          isUnarchiveAction
            ? "האם אתה בטוח שברצונך לשחזר?"
            : "האם אתה בטוח שברצונך למחוק?"
        }
        confirmBtnText="כן"
        cancelBtnText="לא"
      />
    </>
  );
}

export default Header;
